import { takeLatest, put } from 'redux-saga/effects';
import { logOut } from '../auth/actions';
import { apiCall } from '../../effects';
import { getErrorNotification } from '../../util';
import { notify, successfulNotify } from '../notifications/actions';
import {
  clearPromotionCodeList,
  createPromotionCodeFail,
  createPromotionCodeRequest,
  createPromotionCodeSuccess,
  deletePromotionCodeFail,
  deletePromotionCodeRequest,
  deletePromotionCodeSuccess,
  getPromotionCodeFail,
  getPromotionCodeListFail,
  getPromotionCodeListRequest,
  getPromotionCodeListSuccess,
  getPromotionCodeRequest,
  getPromotionCodeSuccess,
  initPromotionCodeFail,
  initPromotionCodeRequest,
  initPromotionCodeSuccess,
  savePromotionCodeFail,
  savePromotionCodeRequest,
  savePromotionCodeSuccess,
  setPromotionCodeListDirty,
  validatePromotionCodeWithProductFail,
  validatePromotionCodeWithProductRequest,
  validatePromotionCodeWithProductSuccess,
  applyPromotionCodeToActiveSubscriptionRequest,
  applyPromotionCodeToActiveSubscriptionFail,
  applyPromotionCodeToActiveSubscriptionSuccess,
} from './actions';

import PromotionCodeService from '../../../services/PromotionCodeService';
import { preparePromotionCode } from './utils';
import BillingService from '../../../services/BillingService';
import { getCurrentUserRequest, getUserRequest } from '../users/actions';
import history from '../../../history';

function* getPromotionCodesList({ payload }: ReturnType<typeof getPromotionCodeListRequest>) {
  try {
    const list = yield apiCall(PromotionCodeService.list, payload);
    yield put(getPromotionCodeListSuccess(list));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(getPromotionCodeListFail());
  }
}

function* getPromotionCode({ payload }: ReturnType<typeof getPromotionCodeRequest>) {
  try {
    const response = yield apiCall(PromotionCodeService.get, payload);
    yield put(getPromotionCodeSuccess(response));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(getPromotionCodeFail());
  }
}

function* savePromotionCode({ payload }: ReturnType<typeof savePromotionCodeRequest>) {
  try {
    const response = yield apiCall(PromotionCodeService.save, payload.id, preparePromotionCode(payload));
    yield put(savePromotionCodeSuccess(response));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(savePromotionCodeFail());
  }
}


function* createPromotionCode({ payload }: ReturnType<typeof createPromotionCodeRequest>) {
  try {
    const response = yield apiCall(PromotionCodeService.create, preparePromotionCode(payload));
    yield put(createPromotionCodeSuccess(response));
  } catch (e) {
    yield put(initPromotionCodeSuccess(payload));
    yield put(notify(getErrorNotification(e)));
    yield put(createPromotionCodeFail());
  }
}

function* deletePromotionCode({ payload }: ReturnType<typeof deletePromotionCodeRequest>) {
  try {
    yield apiCall(PromotionCodeService.delete, payload);
    yield put(deletePromotionCodeSuccess());
    yield put(setPromotionCodeListDirty(true));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(deletePromotionCodeFail());
  }
}

function* getTemplatePromotionCode() {
  try {
    const response = yield apiCall(PromotionCodeService.template);
    yield put(initPromotionCodeSuccess(response));
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(initPromotionCodeFail());
  }
}

function* validatePromotionCodeWithProduct({ payload }: ReturnType<typeof validatePromotionCodeWithProductRequest>) {
  try {
    const response = yield apiCall(PromotionCodeService.verify, payload);
    yield put(validatePromotionCodeWithProductSuccess(response));
  } catch (e) {
    yield put(validatePromotionCodeWithProductFail());
  }
}

function* clearList() {
  yield put(clearPromotionCodeList());
}

function* applyPromotionCodeToActiveSubscription({ payload: { userId, promotionCode } }
: ReturnType<typeof applyPromotionCodeToActiveSubscriptionRequest>) {
  try {
    yield apiCall(BillingService.updatePromotionCodeInActiveSubscription, userId, promotionCode);
    yield put(applyPromotionCodeToActiveSubscriptionSuccess());
    yield put(successfulNotify('Promotion code saved'));
    if (history.location.pathname === '/dashboard/profile/billing/plan') {
      yield put(getCurrentUserRequest());
    } else {
      yield put(getUserRequest(userId));
    }
  } catch (e) {
    yield put(notify(getErrorNotification(e)));
    yield put(applyPromotionCodeToActiveSubscriptionFail());
  }
}

export default [
  takeLatest(getPromotionCodeListRequest.type, getPromotionCodesList),
  takeLatest(getPromotionCodeRequest.type, getPromotionCode),
  takeLatest(savePromotionCodeRequest.type, savePromotionCode),
  takeLatest(createPromotionCodeRequest.type, createPromotionCode),
  takeLatest(deletePromotionCodeRequest.type, deletePromotionCode),
  takeLatest(initPromotionCodeRequest.type, getTemplatePromotionCode),
  takeLatest(validatePromotionCodeWithProductRequest.type, validatePromotionCodeWithProduct),
  takeLatest(logOut.type, clearList),
  takeLatest(applyPromotionCodeToActiveSubscriptionRequest.type, applyPromotionCodeToActiveSubscription),
];
